// app/utils/signature.ts - HMAC-SHA1 签名工具

import HmacSHA1 from 'crypto-js/hmac-sha1'
import MD5 from 'crypto-js/md5'
import Base64 from 'crypto-js/enc-base64'
import Utf8 from 'crypto-js/enc-utf8'

/**
 * 计算请求体的 Content-MD5（Base64 编码）
 */
export function calculateContentMd5(bodyString: string): string {
  const md5 = MD5(Utf8.parse(bodyString))
  return Base64.stringify(md5)
}

/**
 * 获取 GMT 时间字符串（示例：Tue, 08 Oct 2024 12:34:56 GMT）
 */
export function getGMTTime(date: Date = new Date()): string {
  // 使用标准 toUTCString() 输出 GMT 时间
  // e.g. 'Tue, 08 Oct 2024 12:34:56 GMT'
  return date.toUTCString()
}

/**
 * 构建签名字符串
 */
export function buildStringToSign(
  method: string,
  accept: string,
  contentMd5: string,
  contentType: string,
  date: string,
  canonicalizedHeaders: string,
  canonicalizedResource: string
): string {
  return `${method}\n${accept}\n${contentMd5}\n${contentType}\n${date}\n${canonicalizedHeaders}${canonicalizedResource}`
}

/**
 * 计算 HMAC-SHA1 签名并返回 Base64 字符串
 */
export function calculateSignature(stringToSign: string, secretKey: string): string {
  const hmac = HmacSHA1(stringToSign, secretKey)
  return Base64.stringify(hmac)
}

/**
 * 一站式构建签名请求头
 */
export function buildSignatureHeaders(params: {
  method: string
  bodyString: string
  path: string
  accessKey: string
  accessSecret: string
}): {
  Authorization: string
  'Content-MD5': string
  Accept: string
  'Content-Type': string
  'x-cds-signature-nonce': string
} {
  const accept = 'application/json'
  const contentType = 'application/json;charset=utf-8'
  // 在浏览器环境下，Date 属于受限头，无法设置；且服务端签名在无 Date 时按空行计算
  // 为保持与服务端一致性：client 端签名使用空 Date，并且不设置 Date 头；
  // 非浏览器环境（如 Node/Nitro）则正常设置 Date 头
  const isClient = typeof window !== 'undefined'
  const date = isClient ? '' : getGMTTime()
  const nonce = Date.now().toString()
  const contentMd5 = calculateContentMd5(params.bodyString)

  const canonicalizedHeaders = `x-cds-signature-nonce:${nonce}\n`
  const stringToSign = buildStringToSign(
    params.method.toUpperCase(),
    accept,
    contentMd5,
    contentType,
    date,
    canonicalizedHeaders,
    params.path
  )
  const signature = calculateSignature(stringToSign, params.accessSecret)
  const authorization = `cds ${params.accessKey}:${signature}`

  // 仅在非浏览器环境返回 Date 头
  const headers: Record<string, string> = {
    Authorization: authorization,
    'Content-MD5': contentMd5,
    Accept: accept,
    'Content-Type': contentType,
    'x-cds-signature-nonce': nonce
  }
  if (!isClient) {
    headers['Date'] = date
  }
  return headers as any
}
